import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches render-time exceptions from the React tree and shows a static
 * fallback instead of unmounting to a blank white page. Error boundaries must
 * be class components — there is no hook equivalent for componentDidCatch.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <main
          role="alert"
          className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background p-6 text-center"
        >
          <h1 className="text-xl font-semibold">Something went wrong.</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            The page failed to render. Please refresh to try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Reload
          </button>
        </main>
      );
    }
    return this.props.children;
  }
}
